// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, FlatList, Modal, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { Redirect, useLocalSearchParams, useRootNavigationState } from 'expo-router';

import { Text } from '@/components/Themed';
import { useI18n } from '@/i18n';
import { useUser } from '../../user-context';
import { createAppLogger } from '@/services/appLogger';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { useRoundState } from '@/stores/roundState';
import { getCurrentTourId, setCurrentTourId } from '@/services/currentTour';

type Player = {
  id: string;
  name: string;
};

type HoleScore = {
  playerId?: string;
  PlayerId?: string;
  holeNumber?: number;
  HoleNumber?: number;
  hole?: number;
  Hole?: number;
  par?: number;
  Par?: number;
  holePar?: number;
  HolePar?: number;
  strokeIndex?: number;
  StrokeIndex?: number;
  holeIndex?: number;
  HoleIndex?: number;
  grossStrokes?: number | null;
  GrossStrokes?: number | null;
  grossStroke?: number | null;
  GrossStroke?: number | null;
  handicapStrokes?: number | null;
  HandicapStroke?: number | null;
  handicapStroke?: number | null;
  pickup?: boolean;
  Pickup?: boolean;
};

type RoundInfo = {
  courseName?: string;
  courseAddress?: string;
  name?: string;
  description?: string;
};

type RoundSummary = {
  id: string;
  courseName?: string;
  name?: string;
  description?: string;
  status?: string;
  createdAt?: string;
  statusUpdatedAt?: string;
};

type CourseHole = {
  number?: number;
  Number?: number;
  par?: number;
  Par?: number;
  strokeIndex?: number;
  StrokeIndex?: number;
};

const HOLES = Array.from({ length: 18 }, (_, i) => i + 1);
const COL_WIDTH = 44;

export default function ScorecardScreen() {
  const { t } = useI18n();
  const { user } = useUser();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const { tourId: tourParam, roundId: roundIdParam } = useLocalSearchParams<{ tourId?: string; roundId?: string }>();
  const roundId = useRoundState((state) => state.roundId);
  const setRoundId = useRoundState((state) => state.setRoundId);
  const [tourId, setTourId] = useState<string | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [scores, setScores] = useState<HoleScore[]>([]);
  const [holes, setHoles] = useState<CourseHole[]>([]);
  const [roundInfo, setRoundInfo] = useState<RoundInfo | null>(null);
  const [rounds, setRounds] = useState<RoundSummary[]>([]);
  const [roundsLoading, setRoundsLoading] = useState(false);
  const [roundsError, setRoundsError] = useState<string | null>(null);
  const [roundPickerOpen, setRoundPickerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const logToBff = useMemo(
    () =>
      createAppLogger(apiBaseUrl, user?.token, {
        source: 'scorecard',
      }),
    [apiBaseUrl, user?.token]
  );

  useEffect(() => {
    if (!isNavigationReady) return;
    const resolvedTour = tourParam ?? getCurrentTourId();
    setTourId(resolvedTour ?? null);
    if (resolvedTour) {
      setCurrentTourId(resolvedTour);
    }
  }, [isNavigationReady, tourParam]);

  useEffect(() => {
    const resolvedRound = Array.isArray(roundIdParam) ? roundIdParam[0] : roundIdParam;
    if (resolvedRound && resolvedRound !== roundId) {
      setRoundId(resolvedRound);
    }
  }, [roundId, roundIdParam, setRoundId]);

  useEffect(() => {
    if (!tourId || !user?.token || !isNavigationReady) return;
    const loadRounds = async () => {
      setRoundsLoading(true);
      setRoundsError(null);
      try {
        const response = await fetch(`${apiBaseUrl}/api/rounds/tour/${tourId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) {
          throw new Error(`${t('scorecard.selectRoundError')} (${response.status})`);
        }
        const payload = (await response.json()) as RoundSummary[];
        setRounds(payload);
      } catch (err) {
        setRoundsError(t('scorecard.selectRoundError'));
        logToBff('scorecard.rounds.error', { tourId, message: err instanceof Error ? err.message : String(err) }, 'warn');
      } finally {
        setRoundsLoading(false);
      }
    };
    loadRounds();
  }, [apiBaseUrl, isNavigationReady, logToBff, t, tourId, user?.token]);

  useEffect(() => {
    if (!roundId || !user?.token || !isNavigationReady) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [playersRes, scoresRes, roundRes, holesRes] = await Promise.all([
          fetch(`${apiBaseUrl}/api/rounds/${roundId}/players`, {
            headers: { Authorization: `Bearer ${user.token}` },
          }),
          fetch(`${apiBaseUrl}/api/rounds/${roundId}/hole-entries`, {
            headers: { Authorization: `Bearer ${user.token}` },
          }),
          fetch(`${apiBaseUrl}/api/rounds/${roundId}`, {
            headers: { Authorization: `Bearer ${user.token}` },
          }),
          fetch(`${apiBaseUrl}/api/rounds/${roundId}/holes`, {
            headers: { Authorization: `Bearer ${user.token}` },
          }),
        ]);

        if (!playersRes.ok) {
          throw new Error(`${t('scorecard.loadError')} (${playersRes.status})`);
        }
        if (!scoresRes.ok) {
          throw new Error(`${t('scorecard.loadError')} (${scoresRes.status})`);
        }

        const [playerPayload, scorePayload, roundPayload, holesPayload] = await Promise.all([
          playersRes.json(),
          scoresRes.json(),
          roundRes.ok ? roundRes.json() : Promise.resolve(null),
          holesRes.ok ? holesRes.json() : Promise.resolve([]),
        ]);

        const playerData = playerPayload as { players?: Player[]; Players?: Player[] };
        setPlayers(playerData.players ?? playerData.Players ?? []);
        setScores((scorePayload as HoleScore[]) ?? []);
        setRoundInfo(roundPayload as RoundInfo | null);
        setHoles(holesPayload as CourseHole[]);
        logToBff('scorecard.loaded', { roundId });
      } catch (err) {
        setError(t('scorecard.loadError'));
        logToBff('scorecard.load.error', { roundId, message: err instanceof Error ? err.message : String(err) }, 'warn');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [apiBaseUrl, isNavigationReady, logToBff, roundId, t, user?.token]);

  if (!isNavigationReady) return null;
  if (!user) return <Redirect href="/login" />;
  if (!roundId) {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>{t('scorecard.title')}</Text>
        {tourId ? (
          <>
            <Text style={styles.meta}>{t('scorecard.selectRoundTitle')}</Text>
            {roundsLoading ? <Text style={styles.meta}>{t('scorecard.selectRoundLoading')}</Text> : null}
            {roundsError ? <Text style={styles.error}>{roundsError}</Text> : null}
            {rounds.length === 0 && !roundsLoading ? <Text style={styles.meta}>{t('scorecard.selectRoundEmpty')}</Text> : null}
            {rounds.map((round) => (
              <View key={round.id} style={styles.roundSelectRow}>
                <Text style={styles.roundSelectName} numberOfLines={1}>
                  {round.name ?? round.courseName ?? t('scorecard.selectRoundFallback')}
                </Text>
                <Text style={styles.roundSelectMeta}>{round.status ?? '-'}</Text>
                <Text style={styles.roundSelectLink} onPress={() => setRoundId(round.id)}>
                  {t('scorecard.selectRound')}
                </Text>
              </View>
            ))}
          </>
        ) : (
          <Text style={styles.meta}>{t('scorecard.missingRound')}</Text>
        )}
      </View>
    );
  }

  const scoreMap = new Map<string, HoleScore>();
  scores.forEach((score) => {
    const playerId = (score.playerId ?? score.PlayerId)?.toString() ?? '';
    const hole = score.holeNumber ?? score.HoleNumber ?? score.hole ?? score.Hole ?? 0;
    if (!playerId || !hole) return;
    scoreMap.set(`${playerId}:${hole}`, score);
  });

  const holeMeta = new Map<number, { par?: number; strokeIndex?: number }>();
  holes.forEach((hole) => {
    const number = hole.number ?? hole.Number;
    if (!number) return;
    holeMeta.set(number, {
      par: hole.par ?? hole.Par,
      strokeIndex: hole.strokeIndex ?? hole.StrokeIndex,
    });
  });
  scores.forEach((score) => {
    const hole = score.holeNumber ?? score.HoleNumber ?? score.hole ?? score.Hole ?? 0;
    if (!hole) return;
    const existing = holeMeta.get(hole) ?? {};
    const fallbackPar =
      score.par ??
      score.Par ??
      score.holePar ??
      score.HolePar;
    const fallbackStrokeIndex =
      score.strokeIndex ??
      score.StrokeIndex ??
      score.holeIndex ??
      score.HoleIndex;
    if (existing.par == null && existing.strokeIndex == null && fallbackPar == null && fallbackStrokeIndex == null) {
      return;
    }
    holeMeta.set(hole, {
      par: existing.par ?? fallbackPar,
      strokeIndex: existing.strokeIndex ?? fallbackStrokeIndex,
    });
  });

  const renderStrokes = (playerId: string, hole: number) => {
    const score = scoreMap.get(`${playerId}:${hole}`);
    if (!score) return '-';
    const pickup = score.pickup ?? score.Pickup;
    if (pickup) return 'X';
    const strokes = score.grossStrokes ?? score.GrossStrokes ?? score.grossStroke ?? score.GrossStroke;
    const handicap =
      score.handicapStroke ??
      score.HandicapStroke ??
      score.handicapStrokes ??
      score.HandicapStroke;
    if (strokes == null) return '-';
    if (!handicap || handicap <= 0) return `${strokes}`;
    return (
      <View style={styles.strokeCell}>
        <Text style={styles.strokeValue}>{strokes}</Text>
        <Text style={styles.handicapText}>+{handicap}</Text>
      </View>
    );
  };

  const renderStableford = (playerId: string, hole: number) => {
    const par = holeMeta.get(hole)?.par;
    const score = scoreMap.get(`${playerId}:${hole}`);
    const pickup = score?.pickup ?? score?.Pickup;
    const strokes = score?.grossStrokes ?? score?.GrossStrokes ?? score?.grossStroke ?? score?.GrossStroke;
    if (par == null || pickup || strokes == null) return '-';
    return Math.max(0, 2 + par - strokes);
  };

  const grossTotals = new Map<string, number>();
  const stablefordTotals = new Map<string, number>();
  players.forEach((player) => {
    grossTotals.set(player.id, 0);
    stablefordTotals.set(player.id, 0);
  });
  HOLES.forEach((hole) => {
    const par = holeMeta.get(hole)?.par;
    players.forEach((player) => {
      const score = scoreMap.get(`${player.id}:${hole}`);
      const pickup = score?.pickup ?? score?.Pickup;
      const strokes = score?.grossStrokes ?? score?.GrossStrokes ?? score?.grossStroke ?? score?.GrossStroke;
      if (!pickup && strokes != null) {
        grossTotals.set(player.id, (grossTotals.get(player.id) ?? 0) + strokes);
        if (par != null) {
          const points = Math.max(0, 2 + par - strokes);
          stablefordTotals.set(player.id, (stablefordTotals.get(player.id) ?? 0) + points);
        }
      }
    });
  });

  const selectedRound = rounds.find((round) => round.id === roundId) ?? null;

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <Text style={styles.title}>{t('scorecard.title')}</Text>
      {tourId && rounds.length ? (
        <>
          <Text style={styles.meta}>{t('scorecard.selectRoundTitle')}</Text>
          <Pressable style={styles.roundPicker} onPress={() => setRoundPickerOpen(true)}>
            <Text style={styles.roundPickerText} numberOfLines={1}>
              {selectedRound?.name ?? selectedRound?.courseName ?? t('scorecard.selectRoundFallback')}
            </Text>
            <Text style={styles.roundPickerHint}>{t('scorecard.selectRound')}</Text>
          </Pressable>
          <Modal visible={roundPickerOpen} transparent animationType="fade" onRequestClose={() => setRoundPickerOpen(false)}>
            <Pressable style={styles.modalBackdrop} onPress={() => setRoundPickerOpen(false)}>
              <View style={styles.modalCard}>
                <FlatList
                  data={rounds}
                  keyExtractor={(item) => item.id}
                  renderItem={({ item }) => (
                    <Pressable
                      style={[
                        styles.roundOption,
                        item.id === roundId && styles.roundOptionActive,
                      ]}
                      onPress={() => {
                        setRoundId(item.id);
                        setCurrentRoundId(item.id);
                        setRoundPickerOpen(false);
                      }}
                    >
                      <Text style={styles.roundOptionTitle}>
                        {item.name ?? item.courseName ?? t('scorecard.selectRoundFallback')}
                      </Text>
                      <Text style={styles.roundOptionSubtitle}>{item.status ?? '-'}</Text>
                    </Pressable>
                  )}
                  ItemSeparatorComponent={() => <View style={styles.separator} />}
                />
              </View>
            </Pressable>
          </Modal>
        </>
      ) : null}
      {roundInfo?.name || roundInfo?.courseName ? (
        <Text style={styles.meta}>{roundInfo.name ?? roundInfo.courseName}</Text>
      ) : null}
      {roundInfo?.courseAddress ? <Text style={styles.meta}>{roundInfo.courseAddress}</Text> : null}
      {loading ? <ActivityIndicator color="#d5e6ff" /> : null}
      {error ? <Text style={styles.error}>{error}</Text> : null}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.table}>
        <View>
          <View style={styles.headerRow}>
            <Text style={[styles.cell, styles.holeCell]}>{t('scorecard.hole')}</Text>
            <Text style={[styles.cell, styles.metaCell]}>{t('scorecard.par')}</Text>
            <Text style={[styles.cell, styles.metaCell]}>{t('scorecard.index')}</Text>
            {players.map((player) => (
              <Text key={player.id} style={[styles.cell, styles.playerHeaderCell]} numberOfLines={1}>
                {player.name}
              </Text>
            ))}
          </View>
          <View style={styles.subHeaderRow}>
            <Text style={[styles.cell, styles.holeCell, styles.subHeaderText]}>{t('scorecard.hole')}</Text>
            <Text style={[styles.cell, styles.metaCell, styles.subHeaderText]}>{t('scorecard.par')}</Text>
            <Text style={[styles.cell, styles.metaCell, styles.subHeaderText]}>{t('scorecard.index')}</Text>
            {players.map((player) => (
              <View key={`${player.id}-labels`} style={[styles.playerSubHeader, styles.playerSeparator]}>
                <Text style={[styles.cell, styles.scoreCell, styles.subHeaderText]}>{t('scorecard.strokes')}</Text>
                <Text style={[styles.cell, styles.scoreCell, styles.subHeaderText]}>{t('scorecard.points')}</Text>
              </View>
            ))}
          </View>
          {HOLES.map((hole) => {
            const meta = holeMeta.get(hole);
            return (
              <View key={`row-${hole}`} style={styles.row}>
                <Text style={[styles.cell, styles.holeCell]}>{hole}</Text>
                <Text style={[styles.cell, styles.metaCell]}>{meta?.par ?? '-'}</Text>
                <Text style={[styles.cell, styles.metaCell]}>{meta?.strokeIndex ?? '-'}</Text>
                {players.map((player) => (
                  <View key={`${player.id}-${hole}`} style={[styles.playerCells, styles.playerSeparator]}>
                    <Text style={[styles.cell, styles.scoreCell]}>{renderStrokes(player.id, hole)}</Text>
                    <Text style={[styles.cell, styles.scoreCell]}>{renderStableford(player.id, hole)}</Text>
                  </View>
                ))}
              </View>
            );
          })}
          <View style={[styles.row, styles.summaryRow]}>
            <Text style={[styles.cell, styles.holeCell]}>{t('scorecard.total')}</Text>
            <Text style={[styles.cell, styles.metaCell]}>-</Text>
            <Text style={[styles.cell, styles.metaCell]}>-</Text>
            {players.map((player) => (
              <View key={`${player.id}-total`} style={[styles.playerCells, styles.playerSeparator]}>
                <Text style={[styles.cell, styles.scoreCell]}>{grossTotals.get(player.id) ?? '-'}</Text>
                <Text style={[styles.cell, styles.scoreCell]}>{stablefordTotals.get(player.id) ?? '-'}</Text>
              </View>
            ))}
          </View>
        </View>
      </ScrollView>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    padding: 20,
    gap: 12,
    backgroundColor: '#ffffff',
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: '#0b0b0b',
  },
  meta: {
    color: '#4a4a4a',
  },
  error: {
    color: '#ff8c8c',
  },
  roundPicker: {
    borderWidth: 1,
    borderColor: '#d2d6dc',
    borderRadius: 10,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#ffffff',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  roundPickerText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#1b1b1b',
    flex: 1,
  },
  roundPickerHint: {
    fontSize: 12,
    color: '#1c4ed8',
    fontWeight: '700',
    marginLeft: 12,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    padding: 20,
    justifyContent: 'center',
  },
  modalCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 12,
    maxHeight: '80%',
  },
  roundOption: {
    paddingVertical: 10,
    paddingHorizontal: 8,
  },
  roundOptionActive: {
    backgroundColor: '#eef2ff',
    borderRadius: 8,
  },
  roundOptionTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#1b1b1b',
  },
  roundOptionSubtitle: {
    fontSize: 12,
    color: '#4a4a4a',
  },
  separator: {
    height: 1,
    backgroundColor: '#e5e7eb',
  },
  roundSelectRow: {
    borderWidth: 1,
    borderColor: '#d2d6dc',
    borderRadius: 10,
    paddingVertical: 8,
    paddingHorizontal: 10,
    backgroundColor: '#ffffff',
  },
  roundSelectName: {
    fontSize: 14,
    fontWeight: '700',
    color: '#1b1b1b',
  },
  roundSelectMeta: {
    fontSize: 12,
    color: '#4a4a4a',
  },
  roundSelectLink: {
    fontSize: 12,
    color: '#1c4ed8',
    fontWeight: '700',
    marginTop: 4,
  },
  table: {
    paddingVertical: 6,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f1f3f6',
  },
  subHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8f9fb',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
  },
  summaryRow: {
    backgroundColor: '#f7f7f9',
  },
  cell: {
    width: COL_WIDTH,
    textAlign: 'center',
    color: '#121212',
    fontSize: 12,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: '#d2d6dc',
  },
  holeCell: {
    width: COL_WIDTH,
    fontWeight: '700',
  },
  metaCell: {
    width: COL_WIDTH,
  },
  playerHeaderCell: {
    width: COL_WIDTH * 2,
    fontWeight: '700',
  },
  playerSubHeader: {
    flexDirection: 'row',
  },
  playerCells: {
    flexDirection: 'row',
  },
  playerSeparator: {
    borderLeftWidth: 2,
    borderLeftColor: '#4b5563',
  },
  scoreCell: {
    width: COL_WIDTH,
  },
  strokeCell: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
    width: COL_WIDTH,
    height: '100%',
  },
  strokeValue: {
    fontSize: 12,
    color: '#121212',
  },
  subHeaderText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#111827',
  },
  handicapText: {
    position: 'absolute',
    top: 0,
    right: 2,
    fontSize: 8,
    color: '#1f2937',
  },
});
